import React, { useState } from "react";

import PreviewFile from "../../components/modal/PreviewFile";
import {
  getFileDownloadUrl,
  getFilePreviewUrl,
} from "../../slices/DocumentsSlice";
import { FileItem } from "./files.types";
import { errorText } from "./ui/FilesUi";

/**
 * Preview (the existing preview window) and download for a file. Opening a
 * client upload marks it as seen ("New" goes away for everyone).
 */
export const useFileOpener = (
  onError: (message: string) => void,
  onOpened?: (file: FileItem) => void,
) => {
  const [current, setCurrent] = useState<{ name: string; url: string } | null>(
    null,
  );
  const [loading, setLoading] = useState(false);

  const openFile = async (file: FileItem) => {
    onError("");
    setLoading(true);
    setCurrent({ name: file.name, url: "" });
    try {
      const url = await getFilePreviewUrl({ kind: file.kind, id: file.id });
      if (!url) throw new Error("This file couldn't be found in storage.");
      setCurrent({ name: file.name, url });
      onOpened?.(file);
    } catch (e) {
      setCurrent(null);
      onError(errorText(e, "This file couldn't be opened."));
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (file: FileItem) => {
    onError("");
    try {
      const url = await getFileDownloadUrl({ kind: file.kind, id: file.id });
      if (!url) throw new Error("This file couldn't be found in storage.");
      const link = document.createElement("a");
      link.href = url;
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      link.remove();
      onOpened?.(file);
    } catch (e) {
      onError(errorText(e, "This file couldn't be downloaded."));
    }
  };

  const preview = current ? (
    <PreviewFile
      close={() => setCurrent(null)}
      file={current.url}
      fileName={current.name}
      isLoading={loading}
    />
  ) : null;

  return { openFile, downloadFile, preview };
};
